import React, { useCallback, useState } from "react";
import { OmniboxSearchResult } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { FlexRow } from "../Common/__styles__/Layout";
import { EmptyState } from "../Common/EmptyState";
import { Icon } from "../Common/Icons/LucideIcons";
import { Tag } from "../Common/Tag";
import { Body } from "../Common/Typography";
import { CommonContentIconWrapper } from "../Inputs/__styles__/SelectionCard";
import { SelectionCard } from "../Inputs/SelectionCard";
import { RecentSearch, useRecentSearches } from "./useRecentSearches";
import { displayForResult } from "./utils";

const OmniboxRecentSearchItem = ({
  search,
  refreshRecentSearches,
}: {
  search: RecentSearch;
  refreshRecentSearches: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { removeRecentSearch } = useRecentSearches();
  const { title, description, iconName, type } = displayForResult({
    result: search.clickedResult,
    showMatchReason: false,
  });
  return (
    <SelectionCard.Item
      key={search.clickedResult.id}
      size="compact"
      borderStyle="ghost"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      value={search.clickedResult}
    >
      <FlexRow style={{ gap: spacing.m.value }}>
        <SelectionCard.CommonContent
          iconName={iconName}
          title={title}
          description={description}
        />
        <FlexRow style={{ opacity: isHovered ? 1 : 0, gap: spacing.s.value }}>
          {/* TODO: check on tag height */}
          <Tag styleVariant="default">{type}</Tag>
          {/* this should be an ActionButton but we're already inside a button here */}
          <CommonContentIconWrapper
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              removeRecentSearch({
                resultId: search.clickedResult.id,
                resultType: search.clickedResult.__typename,
              });
              refreshRecentSearches();
            }}
          >
            <Icon
              aria-label="Remove recent search"
              iconName="x"
              size={16}
              color="contentSecondary"
            />
          </CommonContentIconWrapper>
        </FlexRow>
      </FlexRow>
    </SelectionCard.Item>
  );
};

export const OmniboxRecentSearches = ({
  handleSearchResultClick,
}: {
  handleSearchResultClick: (searchResult: OmniboxSearchResult) => void;
}) => {
  const { getRecentSearches } = useRecentSearches();
  const [updateCounter, setUpdateCounter] = useState(0);

  const refreshRecentSearches = useCallback(() => {
    setUpdateCounter(n => n + 1);
  }, [setUpdateCounter]);

  const recentSearches = getRecentSearches();
  if (recentSearches.length === 0) {
    return (
      <EmptyState
        icon="no-activity-beach"
        paddingTop={spacing["2xl"].value}
        title="No activity"
        body="Recent searches will appear here."
      />
    );
  }
  return (
    <SelectionCard.Group
      id={`recent-searches-${updateCounter}`}
      direction="vertical"
      spacing="compact"
      handleSelection={handleSearchResultClick}
    >
      <Body type="emphasis" size="small">
        Recent Searches
      </Body>
      {recentSearches.map(search => (
        <OmniboxRecentSearchItem
          key={`${search.clickedResult.id}-${search.clickedResult.__typename}`}
          search={search}
          refreshRecentSearches={refreshRecentSearches}
        />
      ))}
    </SelectionCard.Group>
  );
};
